import React from 'react';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { useForm } from 'react-hook-form';
import { NewAssigneeRow } from './NewAssigneeRow';
import { CurrentlyAssignedAlertMessage } from './CurrentlyAssignedAlertMessage';
import { ConfirmationRow } from './ConfirmationRow';
import { ButtonsRow } from './ButtonsRow';
import { UserService } from '../../../../services/v1/UserService';
import { InterviewTemplateList } from '../../../../entities/v1/applicant_tracking/InterviewTemplate';
import { InterviewList } from '../../../../entities/v1/applicant_tracking/InterviewList';
import { redirectWithAlert } from '../../../../services/FlashService';
import { AlertType } from '../../../../components/Alert';

interface PropTypes {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  interviewTemplateList: InterviewTemplateList;
  interviewList: InterviewList;
  handleClose: (close: boolean) => void;
  userId: number;
}

interface FormValuesSelectOption {
  value: string;
  label: string;
}

export interface DeactivateUserFormValues {
  user?: FormValuesSelectOption;
  confirmation: string;
}

function buildDeactivateUserPayload(
  user: FormValuesSelectOption,
  disableUserId: number,
) {
  return {
    organizerUserId: user ? parseInt(user.value) : null,
    disableUserId,
  };
}

function handleRedirectWithClose(
  handleClose: (close: boolean) => void,
  userId: number,
  message: string,
  type: AlertType,
) {
  handleClose(true);
  redirectWithAlert(
    `/organization_settings/members/${userId}?redirect_uri=${encodeURIComponent(
      '/organization_settings/members',
    )}`,
    message,
    type,
  );
}

export function ModalBody(props: PropTypes) {
  const { handleSubmit, control, register, formState, reset } =
    useForm<DeactivateUserFormValues>({
      mode: 'onChange',
      defaultValues: {
        user: null,
        confirmation: null,
      },
    });

  const onDeactivateUser = async (
    data: DeactivateUserFormValues,
    _e: React.SyntheticEvent,
  ) => {
    props.setIsLoading(true);
    try {
      const payload = buildDeactivateUserPayload(data.user, props.userId);
      await UserService.deactivateUser(payload);

      reset();
      handleRedirectWithClose(
        props.handleClose,
        props.userId,
        'User successfully deactivated!',
        AlertType.Success,
      );
    } catch (e) {
      reset();
      handleRedirectWithClose(
        props.handleClose,
        props.userId,
        'Failed to deactivate user. Please try again later.',
        AlertType.Danger,
      );
    } finally {
      props.setIsLoading(false);
    }
  };

  return props.isLoading ? (
    <LoadingSpinner />
  ) : (
    <>
      <CurrentlyAssignedAlertMessage
        scheduledInterviewsCount={props.interviewList.interviews.length}
        interviewTemplatesCount={
          props.interviewTemplateList.interviewTemplates.length
        }
      />
      <form onSubmit={handleSubmit(onDeactivateUser)}>
        <NewAssigneeRow
          control={control}
          currentUserId={props.userId}
          scheduledInterviewsCount={props.interviewList.interviews.length}
          interviewTemplatesCount={
            props.interviewTemplateList.interviewTemplates.length
          }
        />
        <div className='fs-5 fw-regular mb-3'>
          Do you wish to proceed with the deactivation of this user?
        </div>
        <ConfirmationRow register={register} errors={formState.errors} />
        <ButtonsRow
          onClose={props.handleClose}
          isValid={formState.isValid}
          reset={reset}
        />
      </form>
    </>
  );
}
